import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiRefreshCw, FiDollarSign, FiAlertCircle, FiFileText, FiTrash2 } from 'react-icons/fi';
import jsPDF from 'jspdf';
import {
  fetchExpenses,
  addExpense,
  revertExpense,
  deleteAllExpenses,
  Expense,
} from '../service/financeService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.light.text.primary};
  }

  &.revert:hover {
    color: ${theme.colors.accent.warning};
    background-color: ${theme.colors.accent.warning}20;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const AmountCell = styled.div`
  text-align: right;
  color: ${theme.colors.accent.danger};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ExpensePage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  
  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for expense
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditExpense = isCompanyOwner || (isStaffUser && staffData?.permissions?.expense === 'edit');

  const [loading, setLoading] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [expenseToRevert, setExpenseToRevert] = useState<Expense | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedForPdf, setSelectedForPdf] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'Utilities',
    description: '',
    vendor: '',
    paymentMethod: 'cash',
    ref: '',
  });

  const expectedCompanyId = '';
  const companyId = companyData?.id || expectedCompanyId;


  useEffect(() => {
    if (user) {
      loadExpenses();
    }
  }, [user, companyData]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const data = await fetchExpenses(companyId);
      setExpenses(data);
    } catch (error: any) {
      console.error('Error loading expenses:', error);
      toast.error(error.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await addExpense(companyId, {
        date: new Date(formData.date),
        amount: parseFloat(formData.amount),
        category: formData.category,
        description: formData.description,
        vendor: formData.vendor || '',
        paymentMethod: formData.paymentMethod || '',
        ref: formData.ref || '',
      });

      toast.success('Expense added successfully');
      setShowExpenseModal(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        category: 'Utilities',
        description: '',
        vendor: '',
        paymentMethod: 'cash',
        ref: '',
      });
      await loadExpenses();
    } catch (error: any) {
      console.error('Error adding expense:', error);
      toast.error(error.message || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!expenseToRevert || !revertReason.trim()) {
      toast.error('Please provide a reason for reverting');
      return;
    }

    try {
      await revertExpense(companyId, expenseToRevert.id, revertReason.trim());
      toast.success('Expense reverted successfully');
      setShowRevertModal(false);
      setExpenseToRevert(null);
      setRevertReason('');
      await loadExpenses();
    } catch (error: any) {
      console.error('Error reverting expense:', error);
      toast.error(error.message || 'Failed to revert expense');
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      await deleteAllExpenses(companyId);
      toast.success('All expenses deleted successfully');
      setShowDeleteAllModal(false);
      setSelectedForPdf(new Set());
      await loadExpenses();
    } catch (error: any) {
      console.error('Error deleting all expenses:', error);
      toast.error(error.message || 'Failed to delete all expenses');
    } finally {
      setDeletingAll(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const expenseCategories = [
    'Office Supplies',
    'Transportation',
    'Meals & Entertainment',
    'Utilities',
    'Maintenance',
    'Communication',
    'Miscellaneous',
  ];

  const companyName = companyData?.companyName || companyData?.name || 'Company';
  const toDate = (d: Date | any): string => {
    if (!d) return '—';
    const x = d instanceof Date ? d : d?.toDate ? d.toDate() : new Date(d);
    return x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  /** Normalize expense date to YYYY-MM-DD for grouping. */
  const getDateKey = (d: Date | any): string => {
    if (!d) return '';
    const x = d instanceof Date ? d : d?.toDate ? d.toDate() : new Date(d);
    return x.toISOString().split('T')[0];
  };

  /** Draw one slip for multiple expenses (same date) — one table with one row per expense. */
  const drawSlipForExpenses = (
    pdf: jsPDF,
    expenses: Expense[],
    pageWidth: number,
    yOrigin: number,
    slipHeight: number
  ) => {
    if (expenses.length === 0) return;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const pad = 3;
    let y = yOrigin + margin;
    const first = expenses[0];
    const dateLabel = toDate(first.date);
    const totalAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(companyName.toUpperCase(), margin, y);
    y += 6;
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 5;

    pdf.setFontSize(11);
    pdf.setTextColor(50, 50, 50);
    pdf.text('EXPENSE INVOICE / RECEIPT', margin, y);
    y += 6;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Date: ${dateLabel}`, margin, y);
    pdf.text(`Ref: ${first.ref || first.id.slice(-8).toUpperCase()}`, pageWidth - margin, y, { align: 'right' });
    y += 5;
    pdf.text(`Category: ${first.category || '—'}`, margin, y);
    y += 4;
    pdf.text(`Payment Method: ${first.paymentMethod || '—'}`, margin, y);
    y += 6;

    const colWidths = [26, 78, 20];
    const headers = ['Date', 'Description', 'Amount (Rs.)'];
    const rowHeight = 8;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setFillColor(248, 248, 248);
    pdf.rect(margin, y, contentWidth, rowHeight, 'F');
    let x = margin;
    headers.forEach((h, i) => {
      pdf.text(h, x + pad, y + 5.5);
      x += colWidths[i];
    });
    y += rowHeight;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.15);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);

    expenses.forEach((expense) => {
      const descLines = pdf.splitTextToSize(expense.description || '—', colWidths[1] - pad * 2);
      const lineCount = Math.max(1, descLines.length);
      const dataRowHeight = Math.max(rowHeight, lineCount * 5 + 4);
      pdf.line(margin, y, pageWidth - margin, y);
      pdf.text(toDate(expense.date), margin + pad, y + 5, { maxWidth: colWidths[0] - pad * 2 });
      descLines.forEach((line: string, idx: number) => {
        pdf.text(line, margin + colWidths[0] + pad, y + 5 + idx * 5);
      });
      pdf.text(expense.amount.toLocaleString('en-PK'), pageWidth - margin - pad, y + 5, { align: 'right', maxWidth: colWidths[2] - pad * 2 });
      y += dataRowHeight;
    });

    pdf.line(margin, y, pageWidth - margin, y);
    y += 4;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Total:', margin + colWidths[0] + colWidths[1], y);
    pdf.text(`Rs. ${totalAmount.toLocaleString('en-PK')}`, pageWidth - margin - pad, y, { align: 'right' });
    y += 6;

    const footerY = Math.max(y + 10, yOrigin + slipHeight - 14);
    pdf.setFontSize(7);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Computer generated', pageWidth / 2, footerY - 2, { align: 'center' });
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.15);
    pdf.line(margin, footerY, pageWidth - margin, footerY);
    pdf.text('Expense slip.', pageWidth / 2, footerY + 4, { align: 'center' });
  };

  /** Draw one expense slip into pdf at given y origin. Used for A5 single or each half of A4. */
  const drawOneSlip = (
    pdf: jsPDF,
    expense: Expense,
    pageWidth: number,
    yOrigin: number,
    slipHeight: number
  ) => {
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const pad = 3;
    let y = yOrigin + margin;

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(companyName.toUpperCase(), margin, y);
    y += 6;
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 5;

    pdf.setFontSize(11);
    pdf.setTextColor(50, 50, 50);
    pdf.text('EXPENSE INVOICE / RECEIPT', margin, y);
    y += 6;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Date: ${toDate(expense.date)}`, margin, y);
    pdf.text(`Ref: ${expense.ref || expense.id.slice(-8).toUpperCase()}`, pageWidth - margin, y, { align: 'right' });
    y += 5;
    pdf.text(`Category: ${expense.category || '—'}`, margin, y);
    y += 4;
    pdf.text(`Payment Method: ${expense.paymentMethod || '—'}`, margin, y);
    y += 6;

    const colWidths = [26, 78, 20];
    const headers = ['Date', 'Description', 'Amount (Rs.)'];
    const rowHeight = 8;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setFillColor(248, 248, 248);
    pdf.rect(margin, y, contentWidth, rowHeight, 'F');
    let x = margin;
    headers.forEach((h, i) => {
      pdf.text(h, x + pad, y + 5.5);
      x += colWidths[i];
    });
    y += rowHeight;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.15);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 2;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const descLines = pdf.splitTextToSize(expense.description || '—', colWidths[1] - pad * 2);
    const lineCount = Math.max(1, descLines.length);
    const dataRowHeight = Math.max(rowHeight, lineCount * 5 + 4);
    pdf.rect(margin, y, contentWidth, dataRowHeight, 'S');
    pdf.text(toDate(expense.date), margin + pad, y + 5, { maxWidth: colWidths[0] - pad * 2 });
    descLines.forEach((line: string, idx: number) => {
      pdf.text(line, margin + colWidths[0] + pad, y + 5 + idx * 5);
    });
    pdf.text(expense.amount.toLocaleString('en-PK'), pageWidth - margin - pad, y + 5, { align: 'right', maxWidth: colWidths[2] - pad * 2 });
    y += dataRowHeight + 4;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Total:', margin + colWidths[0] + colWidths[1], y);
    pdf.text(`Rs. ${expense.amount.toLocaleString('en-PK')}`, pageWidth - margin - pad, y, { align: 'right' });
    y += 6;

    if (expense.vendor) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Vendor: ${expense.vendor}`, margin, y);
      y += 4;
    }
    pdf.setTextColor(0, 0, 0);

    // "Computer generated" above the footer line
    const footerY = yOrigin + slipHeight - 14;
    pdf.setFontSize(7);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Computer generated', pageWidth / 2, footerY - 2, { align: 'center' });
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.15);
    pdf.line(margin, footerY, pageWidth - margin, footerY);
    pdf.text('Expense slip.', pageWidth / 2, footerY + 4, { align: 'center' });
  };

  /** Single expense: one A5 PDF. */
  const downloadExpenseInvoicePdf = (expense: Expense) => {
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a5' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    drawOneSlip(pdf, expense, pageWidth, 0, pageHeight);
    pdf.save(`Expense_${expense.id.slice(-6)}_${toDate(expense.date).replace(/\s/g, '_')}.pdf`);
    toast.success('Invoice downloaded');
  };

  const toggleSelectForPdf = (id: string) => {
    setSelectedForPdf((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Group selected expenses by date; same-date expenses go in one table per slip. */
  const handleDownloadSelected = () => {
    const ids = Array.from(selectedForPdf);
    if (ids.length === 0) {
      toast.error('Select at least one expense.');
      return;
    }
    const selected = expenses.filter((e) => ids.includes(e.id));
    const byDate = new Map<string, Expense[]>();
    selected.forEach((e) => {
      const key = getDateKey(e.date);
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key)!.push(e);
    });
    const groups = Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, list]) => list);

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    if (groups.length === 1 && groups[0].length === 1) {
      // Single expense: one A5-style slip
      const small = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a5' });
      const w = small.internal.pageSize.getWidth();
      const h = small.internal.pageSize.getHeight();
      drawOneSlip(small, groups[0][0], w, 0, h);
      small.save(`Expense_${groups[0][0].id.slice(-6)}_${toDate(groups[0][0].date).replace(/\s/g, '_')}.pdf`);
    } else if (groups.length === 1) {
      // One date, multiple expenses: one slip with one table
      drawSlipForExpenses(pdf, groups[0], pageWidth, 0, pageHeight);
      pdf.save(`Expenses_${toDate(groups[0][0].date).replace(/\s/g, '_')}.pdf`);
    } else {
      // Multiple dates: one slip per date, each on its own page
      groups.forEach((group, i) => {
        if (i > 0) pdf.addPage();
        drawSlipForExpenses(pdf, group, pageWidth, 0, pageHeight);
      });
      pdf.save(`Expenses_${groups.length}_dates.pdf`);
    }
    toast.success('Invoice(s) downloaded');
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Expenses</PageTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          {selectedForPdf.size > 0 && (
            <Button variant="secondary" onClick={handleDownloadSelected} title="Download selected; same-date expenses in one table">
              Download selected ({selectedForPdf.size})
            </Button>
          )}
          {canEditExpense && expenses.length > 0 && (
            <Button variant="danger" onClick={() => setShowDeleteAllModal(true)} title="Delete all expenses">
              <FiTrash2 size={18} style={{ marginRight: theme.spacing[2] }} />
              Delete all
            </Button>
          )}
          {canEditExpense && (
            <Button onClick={() => setShowExpenseModal(true)}>
              <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
              Add Expense
            </Button>
          )}
        </div>
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.danger}>
              {formatCurrency(totalExpenses)}
            </StatValue>
            <StatLabel>Total Expenses</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue>{expenses.length}</StatValue>
            <StatLabel>Total Entries</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
              Loading expenses...
            </div>
          ) : expenses.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: 44, textAlign: 'center' }}>
                    <span title="Select for invoice download (same-date in one table)">Select</span>
                  </Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Category</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Vendor</Table.HeaderCell>
                  <Table.HeaderCell>Payment Method</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Amount</Table.HeaderCell>
                  <Table.HeaderCell>Invoice</Table.HeaderCell>
                  {canEditExpense && <Table.HeaderCell>Actions</Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {expenses.map((expense) => (
                  <Table.Row key={expense.id}>
                    <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <input
                        type="checkbox"
                        checked={selectedForPdf.has(expense.id)}
                        onChange={() => toggleSelectForPdf(expense.id)}
                        title="Select for invoice download (same-date expenses in one table)"
                      />
                    </Table.Cell>
                    <Table.Cell>{formatDate(expense.date)}</Table.Cell>
                    <Table.Cell>{expense.category || '—'}</Table.Cell>
                    <Table.Cell>{expense.description || '—'}</Table.Cell>
                    <Table.Cell>{expense.vendor || '—'}</Table.Cell>
                    <Table.Cell>{expense.paymentMethod || '—'}</Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell>{formatCurrency(expense.amount)}</AmountCell>
                    </Table.Cell>
                    <Table.Cell>
                      <ActionButton
                        onClick={() => downloadExpenseInvoicePdf(expense)}
                        title="Download PDF invoice"
                      >
                        <FiFileText size={18} />
                      </ActionButton>
                    </Table.Cell>
                    {canEditExpense && (
                      <Table.Cell>
                        {!expense.reverted && (
                          <ActionButton
                            className="revert"
                            onClick={() => {
                              setExpenseToRevert(expense);
                              setShowRevertModal(true);
                            }}
                            title="Revert"
                          >
                            <FiRefreshCw size={18} />
                          </ActionButton>
                        )}
                        {expense.reverted && (
                          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                            Reverted
                          </span>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <FiDollarSign size={48} style={{ marginBottom: theme.spacing[2], opacity: 0.5 }} />
              <p>No expenses found. Add your first expense to get started.</p>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          setFormData({
            date: new Date().toISOString().split('T')[0],
            amount: '',
            category: 'Utilities',
            description: '',
            vendor: '',
            paymentMethod: 'cash',
            ref: '',
          });
        }}
        title="Add Expense"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowExpenseModal(false);
                setFormData({
                  date: new Date().toISOString().split('T')[0],
                  amount: '',
                  category: 'Utilities',
                  description: '',
                  vendor: '',
                  paymentMethod: 'cash',
                  ref: '',
                });
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const form = document.getElementById('expense-form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                }
              }}
              loading={saving}
              disabled={saving}
            >
              Add Expense
            </Button>
          </>
        }
      >
        <form id="expense-form" onSubmit={handleSubmit}>
          <ModalContent>
            <FormRow>
              <FormGroup>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Amount *</Label>
                <Input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, amount: value });
                    }
                  }}
                  placeholder="0.00"
                  required
                  dark
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Payment Method</Label>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter expense description"
                required
                dark
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Vendor</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  placeholder="Vendor name"
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Reference</Label>
                <Input
                  value={formData.ref}
                  onChange={(e) => setFormData({ ...formData, ref: e.target.value })}
                  placeholder="Reference number"
                  dark
                />
              </FormGroup>
            </FormRow>
          </ModalContent>
        </form>
      </Modal>

      <Modal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setExpenseToRevert(null);
          setRevertReason('');
        }}
        title="Revert Expense"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowRevertModal(false);
                setExpenseToRevert(null);
                setRevertReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRevert}
              disabled={!revertReason.trim()}
            >
              Revert
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3], marginBottom: theme.spacing[4] }}>
          <FiAlertCircle size={24} style={{ color: theme.colors.accent.warning }} />
          <div>
            <p>Are you sure you want to revert this expense? This will create a debit transaction to reverse the original credit.</p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary, marginTop: theme.spacing[2] }}>
              Category: {expenseToRevert?.category}
            </p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
              Amount: {expenseToRevert ? formatCurrency(expenseToRevert.amount) : ''}
            </p>
          </div>
        </div>
        <div>
          <Label>Reason for Revert *</Label>
          <Input
            value={revertReason}
            onChange={(e) => setRevertReason(e.target.value)}
            placeholder="Enter reason for reverting this expense"
            required
            dark
          />
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteAllModal}
        onClose={() => !deletingAll && setShowDeleteAllModal(false)}
        title="Delete all expenses"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteAllModal(false)}
              disabled={deletingAll}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteAll}
              loading={deletingAll}
              disabled={deletingAll}
            >
              Delete all ({expenses.length})
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing[3] }}>
          <FiAlertCircle size={24} style={{ color: theme.colors.accent.danger, flexShrink: 0, marginTop: 2 }} />
          <div>
            <p>Are you sure you want to delete <strong>all {expenses.length} expense(s)</strong>? This will also remove their related transactions. This action cannot be undone.</p>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default ExpensePage;
